import { useEffect, useMemo } from 'react';
import FilterTypes from '@constants/FilterTypes.ts';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import I18n from '@utils/i18n';
import useTenantContext from '@contexts/useTenantContext';
import { actionTypes } from '../reducer';

const isFilterValuePopulated = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value !== '';
};

const useFilters = ({ filters, dispatch, hideAccountFilter, compliancePlatformsForSelect }) => {
  const { tenant } = useTenantContext();
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
  const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.values.territory);

  useEffect(() => {
    if (!hasPrimaryTerritoryScope && isFilterValuePopulated(filters.values.additionalTerritory)) {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'additionalTerritory' });
    }
  }, [hasPrimaryTerritoryScope, filters.values.additionalTerritory, dispatch]);
  const onChangeFilter = (fieldName) => {
    return (value) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
    };
  };

  const handleMultiSelectFilterChange = (fieldName) => {
    return (value) => {
      dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
    };
  };

  const onToggleFilter = (fieldName) => {
    return () => {
      dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
    };
  };

  const filtersList = useMemo(() => {
    const list = [];

    // Only show account filter if not hidden
    if (!hideAccountFilter) {
      list.push({
        field: 'account',
        locale: 'account',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.account,
        value: filters.values.account,
        // IP-15547. A building's account is optional, so offer an Unassigned option the same way the
        // territory filter below does. FilterPanel turns this into `&with_unassigned=true` on both
        // the lookup and value-map URLs; the lookup emits it as value 0.
        withUnassigned: true,
        onChange: handleMultiSelectFilterChange('account'),
        onToggle: onToggleFilter('account')
      });
    }

    list.push(
      {
        field: 'status',
        locale: 'status',
        trueLabel: I18n.t('generic.active'),
        falseLabel: I18n.t('generic.inactive'),
        type: 'booleanSelect',
        active: filters.active.status,
        value: filters.values.status,
        onChange: onChangeFilter('status'),
        onToggle: onToggleFilter('status')
      },
      {
        field: 'tags',
        backendField: 'tags',
        locale: 'building_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.tags,
        value: filters.values.tags,
        onChange: handleMultiSelectFilterChange('tags'),
        onToggle: onToggleFilter('tags')
      },
      {
        field: 'name',
        locale: 'name',
        type: 'textBox',
        active: filters.active.name,
        value: filters.values.name,
        onChange: onChangeFilter('name'),
        onToggle: onToggleFilter('name')
      },
      {
        // Integration data that only some tenants populate, so it ships off by default and is
        // switched on from the filter menu.
        field: 'externalId',
        locale: 'external_id',
        type: 'textBox',
        active: filters.active.externalId,
        value: filters.values.externalId,
        onChange: onChangeFilter('externalId'),
        onToggle: onToggleFilter('externalId')
      }
    );

    if (!globalTerritoriesApplied) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: handleMultiSelectFilterChange('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: handleMultiSelectFilterChange('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    list.push({
      // IP-15560. Options come from /lookup?type=sales_person, which lists only people actually
      // assigned. Active by default: FilterPanel renders only active filters and never calls
      // onToggle, so an inactive filter is unreachable (learned on IP-15509).
      field: 'salesPerson',
      locale: 'sales_person',
      type: FilterTypes.GenericMultiSelect,
      active: filters.active.salesPerson,
      value: filters.values.salesPerson,
      onChange: handleMultiSelectFilterChange('salesPerson'),
      onToggle: onToggleFilter('salesPerson')
    });

    if (compliancePlatformsForSelect?.length > 0) {
      list.push({
        field: 'compliancePlatform',
        locale: 'compliance_platform',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.compliancePlatform,
        value: filters.values.compliancePlatform,
        optionsForSelect: compliancePlatformsForSelect.map((opt) => ({ title: opt.label, value: opt.value })),
        onChange: handleMultiSelectFilterChange('compliancePlatform'),
        onToggle: onToggleFilter('compliancePlatform')
      });
    }

    return list;
  }, [filters, dispatch, globalTerritoriesApplied, hideAccountFilter, compliancePlatformsForSelect, tenant]);

  return filtersList;
};

export default useFilters;
